/**
 * HTTP client for the Certify backend (`/api/v1`).
 *
 * Every request and response is JSON. Admin requests carry the Sanctum bearer
 * token, kept in localStorage so links opened in new tabs stay signed in. The
 * API ends a sign-in after 30 minutes without activity (8 hours at most), so
 * a closed or abandoned tab doesn't stay signed in. Polling should pass
 * `background: true` so it doesn't count as activity.
 * Failures are thrown as `ApiError`, so pages can tell a validation error
 * (422) from rate limiting (429), maintenance (503) or a dropped connection.
 */

const API_URL = (import.meta.env.VITE_API_URL ?? '/api/v1').replace(/\/+$/, '');

const TOKEN_KEY = 'certify.token';

// --- Token -------------------------------------------------------------------

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null): void {
  try {
    if (token === null) localStorage.removeItem(TOKEN_KEY);
    else localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // Storage blocked: the token only lasts until the page reloads.
  }
}

// --- Errors ------------------------------------------------------------------

export class ApiError extends Error {
  /** HTTP status, or 0 when the server couldn't be reached. */
  readonly status: number;
  /** Field errors from a 422, keyed by field name. */
  readonly errors: Record<string, string[]>;
  /** The parsed response body, when there was one. */
  readonly body: unknown;
  /** Seconds to wait before retrying, from a 429's `Retry-After` header. */
  readonly retryAfter: number | null;

  constructor(
    status: number,
    message: string,
    options: { errors?: Record<string, string[]>; body?: unknown; retryAfter?: number | null } = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errors = options.errors ?? {};
    this.body = options.body;
    this.retryAfter = options.retryAfter ?? null;
  }

  get isNetworkError(): boolean {
    return this.status === 0;
  }
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
  get isNotFound(): boolean {
    return this.status === 404;
  }
  get isValidation(): boolean {
    return this.status === 422;
  }
  get isRateLimited(): boolean {
    return this.status === 429;
  }
  get isMaintenance(): boolean {
    return this.status === 503;
  }

  /** The first message for a field, for showing under an input. */
  fieldError(field: string): string | undefined {
    return this.errors[field]?.[0];
  }
}

// --- 401 handling ------------------------------------------------------------

let unauthorizedHandler: (() => void) | null = null;

/**
 * Called when an authenticated request comes back 401 (the token expired or
 * was revoked). The token is already cleared; the handler should send the
 * admin to the login page.
 */
export function onUnauthorized(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

// --- Requests ----------------------------------------------------------------

export type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Query;
  signal?: AbortSignal;
  /** Sent by the app on its own (polling): doesn't keep an idle sign-in alive. */
  background?: boolean;
}

function buildUrl(path: string, query?: Query): string {
  const url = `${API_URL}/${path.replace(/^\/+/, '')}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.append(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function messageFor(status: number, body: unknown): string {
  if (body && typeof body === 'object' && 'message' in body && typeof body.message === 'string' && body.message) {
    return body.message;
  }
  switch (status) {
    case 401:
      return 'Your session has ended. Please sign in again.';
    case 403:
      return 'You are not allowed to do that.';
    case 404:
      return 'Not found.';
    case 429:
      return 'Too many attempts. Please wait a moment and try again.';
    case 503:
      return 'The service is temporarily unavailable. Please try again later.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

/** Sends a request and returns the response if it succeeded; throws `ApiError` otherwise. */
async function send(path: string, options: RequestOptions = {}): Promise<Response> {
  const { method = 'GET', body, query, signal, background } = options;
  const token = getToken();

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;
  if (background) headers['X-Background-Request'] = '1';

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(0, 'Could not reach the server. Check your connection and try again.');
  }

  if (!response.ok) {
    const data = await parseBody(response);
    const retryAfterHeader = response.headers.get('Retry-After');
    const errors =
      data && typeof data === 'object' && 'errors' in data && data.errors && typeof data.errors === 'object'
        ? (data.errors as Record<string, string[]>)
        : undefined;

    // A 401 with a token means the session ended. Without one (e.g. a failed
    // login) it is an ordinary error for the caller to show.
    if (response.status === 401 && token) {
      setToken(null);
      unauthorizedHandler?.();
    }

    throw new ApiError(response.status, messageFor(response.status, data), {
      errors,
      body: data,
      retryAfter: retryAfterHeader ? Number(retryAfterHeader) || null : null,
    });
  }

  return response;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options);
  return (await parseBody(response)) as T;
}

/**
 * Downloads a file the API sends (e.g. a CSV export) and saves it as
 * `filename`. Goes through fetch rather than a link so the admin's token is
 * sent; errors are thrown as `ApiError`, as for any request.
 */
export async function download(path: string, filename: string, query?: Query): Promise<void> {
  const response = await send(path, { query });
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Give the browser a moment to start the download before freeing the file.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const api = {
  get: <T>(path: string, query?: Query, signal?: AbortSignal) =>
    request<T>(path, { query, signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
